import type { IColor } from "react-color-palette";

const HEX_COLOR = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGBA_COLOR = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;

export const DEFAULT_SAVED_COLORS = [
  "#EB3440FF",
  "#F4A261FF",
  "#E9C46AFF",
  "#2A9D8FFF",
  "#D9F75EFF",
  "#2F7BBEFF",
  "#6A4C93FF",
  "#4F5964FF",
  "#F5F7FAFF",
  "#42C9C5FF",
] as const;

export const SAVED_COLOR_SLOT_IDS = Array.from(
  { length: DEFAULT_SAVED_COLORS.length },
  (_, index) => `saved-color-${index + 1}`,
);

function clampChannel(value: number) {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function clampAlpha(value: number) {
  return Math.min(1, Math.max(0, value));
}

function toHexChannel(value: number) {
  return clampChannel(value).toString(16).padStart(2, "0").toUpperCase();
}

function alphaToHex(value: number) {
  return Math.round(clampAlpha(value) * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
}

function rgbaToHex8(red: number, green: number, blue: number, alpha = 1) {
  return `${`#${toHexChannel(red)}${toHexChannel(green)}${toHexChannel(blue)}`}${alphaToHex(alpha)}`;
}

export function normalizeSavedColor(value: string | null | undefined, fallbackColor: string) {
  if (!value) {
    return fallbackColor;
  }

  const trimmedValue = value.trim();

  if (HEX_COLOR.test(trimmedValue)) {
    return trimmedValue.length === 7 ? `${trimmedValue.toUpperCase()}FF` : trimmedValue.toUpperCase();
  }

  const rgbaMatch = trimmedValue.match(RGBA_COLOR);
  if (!rgbaMatch) {
    return fallbackColor;
  }

  const [, red, green, blue, alpha] = rgbaMatch;
  return rgbaToHex8(
    Number.parseInt(red, 10),
    Number.parseInt(green, 10),
    Number.parseInt(blue, 10),
    alpha === undefined ? 1 : Number.parseFloat(alpha),
  );
}

export function normalizeSavedColors(savedColors: string[] | null | undefined) {
  const fallbackColors = [...DEFAULT_SAVED_COLORS];
  if (!savedColors?.length) {
    return fallbackColors;
  }

  return fallbackColors.map((fallbackColor, index) => normalizeSavedColor(savedColors[index], fallbackColor));
}

export function colorToHex8(color: IColor) {
  return rgbaToHex8(color.rgb.r, color.rgb.g, color.rgb.b, color.rgb.a ?? 1);
}
